import { useCallback, useEffect, useMemo, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import useFeaturePreview from '@alltrails/core/hooks/useFeaturePreview';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import type { ConditionStyleId } from '@alltrails/maps/types/Styles';
import { conditionsStyleIds } from '@alltrails/maps/utils/constants';
import { updateIsConditionsPreviewActive } from '../redux/conditionsSlice';
import { useAppDispatch, useAppSelector } from '../redux/helpers';

const useConditionsValidation = () => {
  const dispatch = useAppDispatch();
  const { activeCondition, isConditionsPreviewActive, dynamicStyleIds } = useAppSelector(state => ({
    activeCondition: state.conditions.activeCondition,
    isConditionsPreviewActive: state.conditions.isConditionsPreviewActive,
    dynamicStyleIds: state.map.dynamicStyleIds
  }));
  const user = useUser();

  const [hasShownConditionsUpsellModal, setHasShownConditionsUpsellModal] = useState(false);
  const [showConditionsUpsellModal, setShowConditionsUpsellModal] = useState(false);
  const onConditionsUpsellModalCloseRequest = useCallback(() => setShowConditionsUpsellModal(false), []);
  const [conditionsUpsellModalApplyStylesCallback, setConditionsUpsellModalApplyStylesCallback] = useState<() => void>();

  const onPreviewFinished = useCallback(() => {
    setShowConditionsUpsellModal(true);
    setHasShownConditionsUpsellModal(true);
    dispatch(updateIsConditionsPreviewActive(false));
  }, [dispatch]);

  const { isPreviewActive, requestPreview, cancelPreview } = useFeaturePreview({
    featureEntitlement: 'advanced_conditions',
    onPreviewFinished
  });

  useEffect(() => {
    if (!activeCondition && isPreviewActive) {
      // cancel the timer if the condition overlay is no longer applied
      cancelPreview();
    }
  }, [activeCondition, cancelPreview, isPreviewActive]);

  const removeAppliedConditions = useCallback(() => {
    dispatch(updateDynamicStyleIds(dynamicStyleIds.filter(id => !conditionsStyleIds.includes(id as ConditionStyleId))));
  }, [dispatch, dynamicStyleIds]);

  // This useEffect is for users who apply trail conditions via the entry points on the trail page,
  // as those users can apply trail conditions without going through the normal handleFailedEntitlementCheck flow.
  // If the user isn't actually eligible for the preview, we immediately cancel it.
  useEffect(() => {
    async function startPreview() {
      const { isEligible } = await requestPreview();
      if (!isEligible) {
        cancelPreview();
      }
    }
    if (isConditionsPreviewActive) {
      startPreview();
    }
  }, [cancelPreview, dispatch, isConditionsPreviewActive, requestPreview]);

  const handleConditionsFailedEntitlementCheck = useCallback(
    async (applyStyles: () => void) => {
      const { isEligible } = await requestPreview();
      if (isEligible) {
        applyStyles();
      } else {
        setShowConditionsUpsellModal(true);
        setHasShownConditionsUpsellModal(true);
        setConditionsUpsellModalApplyStylesCallback(() => applyStyles);
      }
    },
    [requestPreview]
  );

  useEffect(() => {
    if (hasShownConditionsUpsellModal && !showConditionsUpsellModal) {
      // After the upsell modal is closed, if the user signed up for peak, apply the conditions styles, otherwise, remove them
      if (user?.featureEntitlements?.includes('advanced_conditions')) {
        conditionsUpsellModalApplyStylesCallback?.();
      } else {
        removeAppliedConditions();
      }
      setConditionsUpsellModalApplyStylesCallback(undefined);
    }
    // We only want to check this when the modal closes.
    // The upsell modals currently do not support an onFailure/onSuccess callback, which would be preferred,
    // and we don't want to make a risky change to those upsells right before peak launch.
  }, [showConditionsUpsellModal]);

  return useMemo(
    () => ({
      showConditionsUpsellModal,
      onConditionsUpsellModalCloseRequest,
      handleConditionsFailedEntitlementCheck,
      removeAppliedConditions
    }),
    [handleConditionsFailedEntitlementCheck, onConditionsUpsellModalCloseRequest, showConditionsUpsellModal, removeAppliedConditions]
  );
};

export default useConditionsValidation;
